/**
 * Flow session types, localStorage persistence, and helpers for `/flow/*` API payloads.
 * Prefer these helpers in UI code instead of branching on raw response shapes.
 */

export interface SessionScreenNavigation {
  can_go_back: boolean;
  can_go_forward: boolean;
  current_index: number;
  total_screens: number;
}

export interface SessionScreenProgress {
  current: number;
  total: number;
  percentage: number;
}

/** Full screen payload as returned inside `screen` or `next_screen`. */
export interface SessionScreen {
  screen_id: string;
  screen_type: string;
  category: string;
  country: string;
  order: number;
  title: string;
  subtitle: string;
  description: string;
  paragraphs: string[];
  bullet_points: string[];
  headings: unknown[];
  questions: unknown[];
  buttons: string[];
  has_illustration: boolean;
  illustration_type?: string;
  navigation: SessionScreenNavigation;
  progress: SessionScreenProgress;
  /** Years that have an in-progress session for this user. Only present on which_year_are_you_filing. */
  in_progress_years?: string[];
  /** When true (get_your_tax_quote after payment), quote-driving inputs are read-only. */
  quote_inputs_locked?: boolean;
}

export interface ResumeInfo {
  screen_id: string;
  last_active: string;
}

/** First response when a session is created (no `success` wrapper). */
export interface QuestionnaireSessionStartResponse {
  session_id: string;
  country: string | null;
  screen: SessionScreen;
  visited_screen_ids: string[] | null;
  initial_answers: unknown | null;
  language?: string;
}

/** Typical response after advancing (current step in `next_screen`). */
export interface QuestionnaireSessionNextResponse {
  success: boolean;
  country: string | null;
  next_screen: SessionScreen | null;
  completed: boolean;
  summary: unknown | null;
  tax_calculation: unknown | null;
  error: string | null;
  resume_info?: ResumeInfo | null;
  /** Present when the flow completes and an App redirect URL was returned. */
  redirect_url?: string | null;
  /** True when App submission failed and user should see a contact-support prompt. */
  show_contact_support?: boolean;
}

/** Typical response when going back (current step in `screen`). */
export interface QuestionnaireSessionBackResponse {
  success: boolean;
  country: string | null;
  screen: SessionScreen;
  error: string | null;
}

export type QuestionnaireSessionResponse =
  | QuestionnaireSessionStartResponse
  | QuestionnaireSessionNextResponse
  | QuestionnaireSessionBackResponse;

export function isSessionStartResponse(
  r: QuestionnaireSessionResponse
): r is QuestionnaireSessionStartResponse {
  return 'session_id' in r && !('success' in r);
}

export function isSessionNextResponse(
  r: QuestionnaireSessionResponse
): r is QuestionnaireSessionNextResponse {
  return 'success' in r && 'next_screen' in r;
}

export function isSessionBackResponse(
  r: QuestionnaireSessionResponse
): r is QuestionnaireSessionBackResponse {
  return 'success' in r && 'screen' in r && !('next_screen' in r);
}

/** Current step regardless of start / forward / back payload shape. */
export function getSessionScreen(r: QuestionnaireSessionResponse): SessionScreen | null {
  if (isSessionNextResponse(r) && r.next_screen) return r.next_screen;
  if ('screen' in r && r.screen) return r.screen;
  return null;
}

export function getSessionId(r: QuestionnaireSessionResponse): string | null {
  return isSessionStartResponse(r) ? r.session_id : null;
}

export function getVisitedScreenIdsFromResponse(r: QuestionnaireSessionResponse): string[] {
  if (isSessionStartResponse(r) && Array.isArray(r.visited_screen_ids)) {
    return r.visited_screen_ids;
  }
  return [];
}

/** localStorage keys aligned with Dynamic Flow docs */
export const FLOW_STORAGE_KEYS = {
  sessionId: 'flow_session_id',
  country: 'flow_country',
  taxYear: 'flow_tax_year',
} as const;

export type FlowMode = 'anonymous' | 'identified';

export type StoredFlowSession = {
  sessionId: string | null;
  country: string | null;
  taxYear: number | null;
};

const EMPTY_STORED: StoredFlowSession = { sessionId: null, country: null, taxYear: null };

/** localStorage key suffix: anonymous → none; identified → `_client_<userId>`. */
function lsSuffix(mode: FlowMode, userId?: string | null): string | null {
  if (mode === 'anonymous') return '';
  const uid = userId?.trim();
  return uid ? `_client_${uid}` : null;
}

function lsKeys(mode: FlowMode, userId?: string | null) {
  const suffix = lsSuffix(mode, userId);
  if (suffix === null) return null;
  return {
    sessionId: `${FLOW_STORAGE_KEYS.sessionId}${suffix}`,
    country: `${FLOW_STORAGE_KEYS.country}${suffix}`,
    taxYear: `${FLOW_STORAGE_KEYS.taxYear}${suffix}`,
  };
}

function readKeys(keys: { sessionId: string; country: string; taxYear: string }): StoredFlowSession {
  const ty = localStorage.getItem(keys.taxYear);
  const taxYear = ty ? parseInt(ty, 10) : null;
  return {
    sessionId: localStorage.getItem(keys.sessionId),
    country: localStorage.getItem(keys.country),
    taxYear: taxYear !== null && !Number.isNaN(taxYear) ? taxYear : null,
  };
}

export function readStoredFlowSession(
  mode: FlowMode = 'anonymous',
  userId?: string | null,
): StoredFlowSession {
  const keys = lsKeys(mode, userId);
  return keys ? readKeys(keys) : EMPTY_STORED;
}

export function persistFlowSession(
  sessionId: string,
  country: string | null,
  taxYear: number,
  mode: FlowMode = 'anonymous',
  userId?: string | null,
): void {
  const keys = lsKeys(mode, userId);
  if (!keys) return;
  localStorage.setItem(keys.sessionId, sessionId);
  if (country) localStorage.setItem(keys.country, country);
  else localStorage.removeItem(keys.country);
  localStorage.setItem(keys.taxYear, String(taxYear));
}

export function clearFlowSessionStorage(mode: FlowMode = 'anonymous', userId?: string | null): void {
  const keys = lsKeys(mode, userId);
  if (!keys) return;
  localStorage.removeItem(keys.sessionId);
  localStorage.removeItem(keys.country);
  localStorage.removeItem(keys.taxYear);
}

/** Move old shared `flow_*_client` keys to the logging-in user (one-time). */
export function migrateLegacyFlowSession(userId: string): void {
  const uid = userId.trim();
  if (!uid) return;
  const old = readKeys({
    sessionId: `${FLOW_STORAGE_KEYS.sessionId}_client`,
    country: `${FLOW_STORAGE_KEYS.country}_client`,
    taxYear: `${FLOW_STORAGE_KEYS.taxYear}_client`,
  });
  if (old.sessionId && !readStoredFlowSession('identified', uid).sessionId) {
    persistFlowSession(old.sessionId, old.country, old.taxYear ?? new Date().getFullYear() - 1, 'identified', uid);
  }
  localStorage.removeItem(`${FLOW_STORAGE_KEYS.sessionId}_client`);
  localStorage.removeItem(`${FLOW_STORAGE_KEYS.country}_client`);
  localStorage.removeItem(`${FLOW_STORAGE_KEYS.taxYear}_client`);
}

type QuestionLike = { id?: string; field_type?: string; current_value?: unknown };

/** Merge `current_value` from each question into `answers` (checkbox → array). */
export function mergeQuestionCurrentValuesIntoAnswers(
  questions: QuestionLike[],
  answers: Record<string, unknown>
): Record<string, unknown> {
  const next = { ...answers };
  for (const q of questions) {
    if (!q?.id) continue;
    if (q.field_type === 'checkbox') {
      next[q.id] = Array.isArray(q.current_value) ? q.current_value : next[q.id] ?? [];
    } else if (q.current_value !== undefined && q.current_value !== null) {
      const cv = q.current_value as unknown;
      // `tax_year` options from the API are strings; normalize so the select matches `current_value`.
      next[q.id] = q.id === 'tax_year' ? String(cv).trim() : cv;
    }
  }
  return next;
}

/** Drop non-JSON-serializable values (e.g. File) before POST /flow/next. */
export function serializeAnswersForFlowApi(answers: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(answers)) {
    if (v instanceof File) continue;
    out[k] = v;
  }
  return out;
}
